import React, { forwardRef } from "react";
import "./IconButton.scss";

// Fontaine M12 — icon-only control. Visual contract: Style Guide V2 `icon-default` / `icon-ghost`.
const ICON_BUTTON_SIZES = ["sm", "md", "lg", "xl"];
const ICON_BUTTON_VARIANTS = ["default", "ghost"];
// Legacy values kept for existing call sites until Phase 3 (registry: legacy).
const LEGACY_VARIANTS = { back: "default" };
const LEGACY_SIZES = { "2xl": "xl" };

/**
 * @param {"default"|"ghost"} [variant="default"]
 * @param {"sm"|"md"|"lg"|"xl"} [size="md"]   28 · 32 · 40 · 44 — same scale as Button; touch hit area ≥ 44px on phones
 * @param {string} label                        REQUIRED accessible name (also accepted as aria-label)
 * @param {boolean} [pressed]                   toggle state (grid/list, filter on) → aria-pressed
 * @param {boolean} [round]                     pill instead of control radius (avatars, floating actions)
 * @param {boolean} [loading]                   disables + aria-busy
 * @param {string} [href]                      renders an <a> (mailto:, full-page links) with the same look
 */
const IconButton = forwardRef(function IconButton(
  {
    children,
    size = "md",
    variant = "default",
    label,
    "aria-label": ariaLabel,
    pressed,
    round = false,
    loading = false,
    disabled = false,
    className = "",
    type = "button",
    href,
    ...rest
  },
  ref
) {
  const name = ariaLabel || label;
  if (import.meta.env?.DEV && !name && !rest["aria-labelledby"]) {
    console.error("IconButton: an accessible name is required — pass label=\"…\" (icon-only controls have no text).");
  }
  const resolvedSize = ICON_BUTTON_SIZES.includes(size) ? size : LEGACY_SIZES[size] || "md";
  const resolvedVariant = ICON_BUTTON_VARIANTS.includes(variant) ? variant : LEGACY_VARIANTS[variant] || "default";
  const classes = [
    "ft-iconbtn",
    `ft-iconbtn--${resolvedSize}`,
    resolvedVariant === "ghost" ? "ft-iconbtn--ghost" : "",
    round ? "ft-iconbtn--round" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (href) {
    const inert = disabled || loading;
    return (
      <a
        ref={ref}
        href={inert ? undefined : href}
        className={classes}
        aria-label={name}
        aria-busy={loading || undefined}
        aria-disabled={inert || undefined}
        tabIndex={inert ? -1 : undefined}
        {...rest}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      aria-label={name}
      aria-pressed={typeof pressed === "boolean" ? pressed : undefined}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      disabled={disabled}
      {...rest}
    >
      {children}
    </button>
  );
});

export default IconButton;
